import { clampRect } from '@/core/geometry'
import { drawSourceToImageData, enhancePreparedOcrImage, type PreparedOcrImage } from '@/core/image'
import type { AttributeFieldRegion, AttributeMetric, NormalizedRect } from '@/domain/types'
import { parseExpText, parseJobName, parseResource } from './parsers'
import { parseLevelDigits } from './level-image'

export const attributeMetrics = ['level', 'jobName', 'exp', 'hp', 'mp'] as const
export function isAttributeMetric(metric: string): metric is AttributeMetric {
  return (attributeMetrics as readonly string[]).includes(metric)
}

const labels: Record<AttributeMetric, RegExp> = {
  level: /^(?:等级|等級)\s*[:：]?\s*/u,
  jobName: /^职业\s*[:：]?\s*|^職業\s*[:：]?\s*/u,
  exp: /^(?:经验值?|經驗值?)\s*[:：]?\s*/u,
  hp: /^(?:血量|HP)\s*[:：]?\s*/iu,
  mp: /^(?:魔量|MP)\s*[:：]?\s*/iu,
}

export function parseAttributeText(metric: AttributeMetric, text: string) {
  const value = text.normalize('NFKC').trim().replace(labels[metric], '')
  if (metric === 'level') return parseLevelDigits(value)
  if (metric === 'jobName') return parseJobName(value)
  if (metric === 'exp') {
    const parsed = parseExpText(value)
    return parsed.ambiguous || parsed.value?.absolute == null || parsed.value.ratio == null ? null
      : { ...parsed.value, percentagePrecision: parsed.percentage!.precision, percentageRounding: 'floor' as const }
  }
  if (!/^[\dOoIl|,]+\s*\/\s*[\dOoIl|,]+$/.test(value)) return null
  const resource = parseResource(value)
  return resource && resource.current !== null && resource.max !== null && resource.max > 0
    && resource.current <= resource.max ? resource : null
}

export function prepareAttributeImage(source: PreparedOcrImage): PreparedOcrImage {
  return enhancePreparedOcrImage(source, { scale: 4, padding: 3, contrast: 1, sharpen: 0, interpolation: 'smooth' })
}

const pink = (r: number, g: number, b: number) => r > 150 && r > g + 35 && b > g + 15 && b < r + 15
const labelInk = (r: number, g: number, b: number) => Math.min(r, g, b) > 200 && Math.max(r, g, b) - Math.min(r, g, b) < 30

/** Save only the fixed label, never changing HP/MP/EXP digits, as visibility evidence. */
export function calibrateAttributeField(frame: ImageData, rect: NormalizedRect): AttributeFieldRegion | null {
  const centerY = Math.round((rect.y + rect.height / 2) * frame.height)
  const left = Math.max(0, Math.floor(rect.x * frame.width) - Math.ceil(frame.width * .18))
  const right = Math.min(frame.width - 1, Math.ceil((rect.x + rect.width) * frame.width))
  const row = Math.max(0, Math.min(frame.height - 1, centerY))
  const runs: { left: number; right: number }[] = []
  let start = -1, last = -1
  for (let x = left; x <= right; x++) {
    let count = 0
    for (let y = Math.max(0, row - 3); y <= Math.min(frame.height - 1, row + 3); y++) {
      const i = (y * frame.width + x) * 4
      if (pink(frame.data[i]!, frame.data[i + 1]!, frame.data[i + 2]!)) count++
    }
    if (count >= 2) { if (start < 0) start = x; last = x }
    // Bridge the white glyphs inside the pink label; otherwise the anchor
    // degenerates into the empty pink tail shared by every attributes row.
    if ((x - last > 12 || x === right) && start >= 0) {
      if (last - start >= 12) runs.push({ left: start, right: last })
      start = -1
    }
  }
  const label = runs.filter(run => run.left < (rect.x + rect.width / 2) * frame.width).at(-1)
  if (!label) return null
  const hasPink = (y: number) => {
    let count = 0
    for (let x = label.left; x <= label.right; x++) {
      const i = (y * frame.width + x) * 4
      if (pink(frame.data[i]!, frame.data[i + 1]!, frame.data[i + 2]!)) count++
    }
    return count > (label.right - label.left) * .25
  }
  let top = row, bottom = row
  while (top > 0 && row - top < 20 && hasPink(top - 1)) top--
  while (bottom < frame.height - 1 && bottom - row < 20 && hasPink(bottom + 1)) bottom++
  if (bottom - top < 6 || bottom - top > 30) return null
  const anchor = { x: label.left / frame.width, y: top / frame.height,
    width: (label.right - label.left + 1) / frame.width, height: (bottom - top + 1) / frame.height }
  // Crop the label out when a user selects the complete row.
  const valueLeft = Math.max(rect.x, (label.right + 2) / frame.width)
  const valueRect = clampRect({ ...rect, x: valueLeft, width: rect.x + rect.width - valueLeft })
  if (valueRect.width * frame.width < 4) return null
  return { rect, valueRect, anchor, signature: attributeSignature(drawSourceToImageData(frame, frame.width, frame.height, anchor)) }
}

function attributeSignature(image: ImageData): number[] {
  const values: number[] = []
  for (let y = 0; y < 8; y++) for (let x = 0; x < 24; x++) {
    const i = (Math.min(image.height - 1, Math.floor((y + .5) / 8 * image.height)) * image.width
      + Math.min(image.width - 1, Math.floor((x + .5) / 24 * image.width))) * 4
    values.push(image.data[i]!, image.data[i + 1]!, image.data[i + 2]!)
  }
  return values
}

export function attributeFieldVisible(frame: ImageData | ImageBitmap | HTMLVideoElement,
  width: number, height: number, region: AttributeFieldRegion): boolean {
  if (!Array.isArray(region.signature) || region.signature.length !== 576 || !region.anchor || !region.valueRect) return false
  if (![region.anchor, region.valueRect].every(rect => [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)
    && rect.x >= 0 && rect.y >= 0 && rect.width > 0 && rect.height > 0 && rect.x + rect.width <= 1.001 && rect.y + rect.height <= 1.001)) return false
  const image = drawSourceToImageData(frame, width, height, region.anchor)
  const actual = attributeSignature(image)
  let difference = 0, changed = 0, colored = 0, changedInk = 0
  for (let i = 0; i < actual.length; i++) {
    const delta = Math.abs(actual[i]! - region.signature[i]!)
    difference += delta
    if (delta > 50) changed++
    if (i % 3 === 0 && pink(actual[i]!, actual[i + 1]!, actual[i + 2]!)) colored++
    if (i % 3 === 0 && labelInk(actual[i]!, actual[i + 1]!, actual[i + 2]!)
      !== labelInk(region.signature[i]!, region.signature[i + 1]!, region.signature[i + 2]!)) changedInk++
  }
  // Pink backgrounds repeat on every row. Match the fixed label's lettering
  // too, so moving by exactly one row cannot silently select a different field.
  return colored > 35 && difference / actual.length < 22 && changed / actual.length < .18 && changedInk / 192 < .035
}
